import type { HelpStep } from "newui/components/ui/Help";
import {
	TOUR_USER_DEACTIVATED,
	TOUR_USER_INVITED,
} from "./tourFixtures";

/**
 * Guided tour steps for the User Management page.
 *
 * Flow:
 *   1. Highlight "Invite User" → open the invite drawer.
 *   2. Walk through the identity step (name/email/phone).
 *   3. Highlight "Create & configure" → close the drawer.
 *   4. Point at the user list.
 *   5–7. Use injected dummy rows to demonstrate resend / deactivate / reactivate.
 *
 * Steps that depend on the drawer being open drive the UI via
 * `onBeforeStep` / `onAfterStep`; `useGuidedTour` waits for each target
 * to be laid out before advancing Joyride.
 *
 * PRO-730: steps 2 and 3 previously targeted `invite-user-form` /
 * `user-drawer-send-invite` — both live in `UserDrawer`'s *configure* phase,
 * which only mounts after the identity form's draft has actually been
 * created (a real API call). Opening the drawer alone (step 1's
 * `onAfterStep`) only ever reveals the *identity* phase
 * (`user-drawer-identity` / `invite-create-draft-button`), so those targets
 * could never mount and Joyride's TARGET_NOT_FOUND tore the whole tour down.
 * The tour deliberately never drives a real invite creation mid-demo (see
 * the dummy `TOUR_USERS` rows used by steps 5-7 for the same reason), so
 * steps 2 and 3 now target the identity phase, which is reachable with no
 * side effects.
 *
 * PRO-730 (follow-up): step 2 targeted `user-drawer-identity`, which is only
 * `DrawerBody` — the spotlight covered the form but not the header/footer, so
 * the drawer's close button and title sat outside the highlighted area. The
 * whole slide-out is `DrawerContent` (`user-drawer-panel`); step 2 now targets
 * that instead so the spotlight covers the entire drawer.
 */
export interface UserListSteps {
	closeInviteDrawer: () => void;
	openInviteDrawer: () => void;
}

export const buildUserListSteps = ({
	openInviteDrawer,
	closeInviteDrawer,
}: UserListSteps): HelpStep[] => [
		{
			target: '[data-testid="invite-user-button"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Invite a new user</h3>
					<p>
						Start here to invite a teammate to your company. We&apos;ll open the
						invite drawer for you on the next step.
					</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
			onAfterStep: () => {
				openInviteDrawer();
			},
		},
		{
			target: '[data-testid="user-drawer-panel"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Fill in the details</h3>
					<p>
						Enter the user&apos;s name, email and phone number. Their email
						domain must match your company — we&apos;ll warn you if it
						doesn&apos;t.
					</p>
				</div>
			),
			placement: "left",
			disableBeacon: true,
			spotlightPadding: 12,
		},
		{
			target: '[data-testid="invite-create-draft-button"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Create &amp; configure</h3>
					<p>
						Click here to save these details. Next you&apos;ll be able to set
						roles and access before sending the invite — the user will appear
						in the table with a status of &apos;invited&apos; once you do.
					</p>
				</div>
			),
			placement: "top",
			disableBeacon: true,
			onAfterStep: () => {
				closeInviteDrawer();
			},
		},
		{
			target: `[data-testid="user-action-resend-${TOUR_USER_INVITED.id}"]`,
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Resend an invite</h3>
					<p>
						If a user&apos;s invite expires (after 72 hours) or they&apos;ve lost
						the email, click the mail icon to send a fresh invite link.
					</p>
				</div>
			),
			placement: "left",
			disableBeacon: true,
		},
		{
			target: `[data-testid="user-action-deactivate-${TOUR_USER_INVITED.id}"]`,
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Deactivate a user</h3>
					<p>
						Click the red icon to revoke a user&apos;s access. They&apos;ll no
						longer be able to log in, but their account is preserved so you can
						reactivate them later.
					</p>
				</div>
			),
			placement: "left",
			disableBeacon: true,
		},
		{
			target: `[data-testid="user-action-reactivate-${TOUR_USER_DEACTIVATED.id}"]`,
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Reactivate a user</h3>
					<p>
						Deactivated users show a reactivate icon. Click it to send them a
						fresh invite — once they finish registration they&apos;ll regain
						access.
					</p>
				</div>
			),
			placement: "left",
			disableBeacon: true,
		},
	];
